import React, { useEffect, useRef } from 'react';

const CelebrationOverlay = ({ onDone }) => {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const particlesRef = useRef([]);
  const rocketsRef = useRef([]);
  const emojisRef = useRef([]);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const W = canvas.width;
    const H = canvas.height;

    const COLORS = [
      '#FF3366', '#FF6B35', '#FFD700', '#00E676',
      '#00B0FF', '#D500F9', '#FF1744', '#76FF03',
      '#FFEA00', '#FF6D00', '#40C4FF', '#E040FB',
    ];

    const rand = (min, max) => Math.random() * (max - min) + min;
    const pick = arr => arr[Math.floor(Math.random() * arr.length)];

    // Confetti particle
    class Confetti {
      constructor(x, y, vx, vy) {
        this.x = x; this.y = y;
        this.vx = vx || rand(-6, 6);
        this.vy = vy || rand(-14, -4);
        this.color = pick(COLORS);
        this.w = rand(6, 14);
        this.h = rand(3, 7);
        this.rot = rand(0, Math.PI * 2);
        this.rotV = rand(-0.2, 0.2);
        this.gravity = 0.35;
        this.alpha = 1;
        this.life = rand(80, 140);
        this.age = 0;
      }
      update() {
        this.vy += this.gravity;
        this.x += this.vx;
        this.y += this.vy;
        this.rot += this.rotV;
        this.age++;
        this.alpha = Math.max(0, 1 - this.age / this.life);
      }
      draw(ctx) {
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rot);
        ctx.fillStyle = this.color;
        ctx.fillRect(-this.w / 2, -this.h / 2, this.w, this.h);
        ctx.restore();
      }
      dead() { return this.age >= this.life; }
    }

    // Sparkle star
    class Sparkle {
      constructor(x, y) {
        this.x = x || rand(0, W);
        this.y = y || rand(0, H * 0.6);
        this.vx = rand(-1.5, 1.5);
        this.vy = rand(-2, 0.5);
        this.size = rand(3, 8);
        this.color = pick(['#FFD700', '#FFFFFF', '#FFE066', '#FFF9C4', '#FFFDE7']);
        this.alpha = 1;
        this.life = rand(40, 80);
        this.age = 0;
        this.twinkle = rand(0, Math.PI * 2);
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.age++;
        this.twinkle += 0.2;
        this.alpha = Math.max(0, 1 - this.age / this.life) * (0.7 + 0.3 * Math.sin(this.twinkle));
      }
      draw(ctx) {
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 6;
        const s = this.size;
        ctx.translate(this.x, this.y);
        ctx.beginPath();
        for (let i = 0; i < 4; i++) {
          ctx.rotate(Math.PI / 2);
          ctx.moveTo(0, 0);
          ctx.lineTo(0, s);
          ctx.lineTo(s * 0.3, s * 0.3);
        }
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      dead() { return this.age >= this.life; }
    }

    // Rocket + burst
    class Rocket {
      constructor() {
        this.x = rand(W * 0.1, W * 0.9);
        this.y = H;
        this.vy = rand(-14, -10);
        this.vx = rand(-1.5, 1.5);
        this.color = pick(COLORS);
        this.trail = [];
        this.exploded = false;
        this.targetY = rand(H * 0.1, H * 0.45);
      }
      update(particles) {
        if (!this.exploded) {
          this.trail.push({ x: this.x, y: this.y, a: 1 });
          if (this.trail.length > 12) this.trail.shift();
          this.x += this.vx;
          this.y += this.vy;
          this.vy += 0.3;
          if (this.y <= this.targetY || this.vy >= 0) {
            this.exploded = true;
            this.burst(particles);
          }
        }
      }
      burst(particles) {
        const count = Math.floor(rand(60, 100));
        for (let i = 0; i < count; i++) {
          const angle = (i / count) * Math.PI * 2;
          const speed = rand(2, 9);
          particles.push(new Confetti(
            this.x, this.y,
            Math.cos(angle) * speed,
            Math.sin(angle) * speed - 2
          ));
          if (i % 4 === 0) {
            particles.push(new Sparkle(this.x + rand(-20, 20), this.y + rand(-20, 20)));
          }
        }
      }
      draw(ctx) {
        if (this.exploded) return;
        this.trail.forEach((t, i) => {
          ctx.save();
          ctx.globalAlpha = (i / this.trail.length) * 0.5;
          ctx.fillStyle = this.color;
          ctx.shadowColor = this.color;
          ctx.shadowBlur = 4;
          ctx.beginPath();
          ctx.arc(t.x, t.y, 2.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        });
        ctx.save();
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(this.x, this.y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      dead() { return this.exploded; }
    }

    // Floating emoji
    class FloatingEmoji {
      constructor() {
        const EMOJIS = ['🎉', '🎊', '✨', '🥳', '🎆', '🎇', '💥', '🌟', '🎈', '🎁'];
        this.emoji = pick(EMOJIS);
        this.x = rand(0, W);
        this.y = H + 30;
        this.vy = rand(-3, -1.5);
        this.vx = rand(-1, 1);
        this.size = rand(20, 42);
        this.rot = rand(0, Math.PI * 2);
        this.rotV = rand(-0.04, 0.04);
        this.alpha = 1;
        this.life = rand(100, 180);
        this.age = 0;
      }
      update() {
        this.x += this.vx;
        this.y += this.vy;
        this.rot += this.rotV;
        this.age++;
        this.alpha = this.age < this.life - 30
          ? Math.min(1, this.age / 20)
          : Math.max(0, (this.life - this.age) / 30);
      }
      draw(ctx) {
        ctx.save();
        ctx.globalAlpha = this.alpha;
        ctx.translate(this.x, this.y);
        ctx.rotate(this.rot);
        ctx.font = `${this.size}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this.emoji, 0, 0);
        ctx.restore();
      }
      dead() { return this.age >= this.life; }
    }

    // Burst from sides/top
    const burstFromEdge = (particles) => {
      const sides = ['left', 'right', 'top'];
      const side = pick(sides);
      let x, y, angle;
      if (side === 'left') { x = 0; y = rand(H * 0.1, H * 0.5); angle = rand(-0.3, 0.3); }
      else if (side === 'right') { x = W; y = rand(H * 0.1, H * 0.5); angle = Math.PI + rand(-0.3, 0.3); }
      else { x = rand(W * 0.2, W * 0.8); y = 0; angle = Math.PI / 2 + rand(-0.3, 0.3); }
      const count = 30;
      for (let i = 0; i < count; i++) {
        const spread = rand(-0.6, 0.6);
        const speed = rand(4, 12);
        particles.push(new Confetti(
          x, y,
          Math.cos(angle + spread) * speed,
          Math.sin(angle + spread) * speed
        ));
      }
      for (let i = 0; i < 5; i++) {
        particles.push(new Sparkle(x + rand(-40, 40), y + rand(-40, 40)));
      }
    };

    let frame = 0;
    const DURATION = 4500; // ms

    const loop = () => {
      const elapsed = Date.now() - startTimeRef.current;
      ctx.clearRect(0, 0, W, H);

      // Spawn rockets early
      if (frame % 18 === 0 && elapsed < 3000) {
        rocketsRef.current.push(new Rocket());
      }

      // Burst from edges
      if (frame % 22 === 0 && elapsed < 3500) {
        burstFromEdge(particlesRef.current);
      }

      // Continuous confetti rain from top
      if (frame % 3 === 0 && elapsed < 3800) {
        for (let i = 0; i < 3; i++) {
          particlesRef.current.push(new Confetti(rand(0, W), -10, rand(-3, 3), rand(1, 4)));
        }
      }

      // Sparkles
      if (frame % 5 === 0 && elapsed < 3500) {
        for (let i = 0; i < 2; i++) {
          particlesRef.current.push(new Sparkle());
        }
      }

      // Emojis
      if (frame % 12 === 0 && elapsed < 3200) {
        emojisRef.current.push(new FloatingEmoji());
      }

      // Update & draw rockets
      rocketsRef.current = rocketsRef.current.filter(r => {
        r.update(particlesRef.current);
        r.draw(ctx);
        return !r.dead();
      });

      // Update & draw particles
      particlesRef.current = particlesRef.current.filter(p => {
        p.update();
        p.draw(ctx);
        return !p.dead();
      });

      // Update & draw emojis
      emojisRef.current = emojisRef.current.filter(e => {
        e.update();
        e.draw(ctx);
        return !e.dead();
      });

      frame++;

      if (elapsed < DURATION) {
        animRef.current = requestAnimationFrame(loop);
      } else {
        // Fade out remaining particles then call onDone
        setTimeout(() => {
          cancelAnimationFrame(animRef.current);
          onDone && onDone();
        }, 600);
      }
    };

    // Kick off immediately with a big burst
    for (let i = 0; i < 80; i++) {
      particlesRef.current.push(new Confetti(rand(0, W), rand(-20, H * 0.3)));
    }
    for (let i = 0; i < 15; i++) {
      particlesRef.current.push(new Sparkle());
    }
    for (let i = 0; i < 5; i++) {
      rocketsRef.current.push(new Rocket());
    }
    for (let i = 0; i < 6; i++) {
      emojisRef.current.push(new FloatingEmoji());
    }

    animRef.current = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(animRef.current);
  }, [onDone]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: 99999,
      }}
    />
  );
};

export default CelebrationOverlay;